import React, { useCallback, useEffect, useRef } from 'react';

type CubicBezier = [number, number, number, number];

interface FlyOut {
  /** Where the drag ends up, in px down, from where it was let go. */
  to: (y: number) => number;
  /** In ms. */
  duration: number;
  ease: CubicBezier;
}

interface UseDragDismissOptions {
  /**
   * The element the drag is written to, as unitless px: `--drag-y` is how far down it's dragged, and
   * `--drag-exit` goes from 0 to 1 over the fly-out. `data-dragging` is set on it while either is moving,
   * so CSS can leave it untransformed at rest.
   */
  ref: React.RefObject<HTMLElement>;
  /** How much of an upward drag it follows, from 0 (none) to 1 (all of it). Down always follows the pointer. */
  upElastic: number;
  /** How far down, in px, the pointer has to have moved when it's let go to dismiss. */
  dismissDistance: number;
  /** Or how fast it's moving down when it's let go, in px per second. */
  dismissVelocity?: number;
  flyOut: FlyOut;
  /**
   * Called when a release dismisses, with a promise that settles once the fly-out ends. With reduced
   * motion there's no fly-out: the drag stays where it was let go and the promise settles right away.
   */
  onDismiss: (flownOut: Promise<void>) => void;
}

interface DragSession {
  pointerId: number;
  startX: number;
  startY: number;
  // where the drag was when the pointer went down, so a drag caught mid snap back carries on from there
  origin: number;
  started: boolean;
  history: { y: number; time: number }[];
}

// how far the pointer moves before it's a drag
const START_DISTANCE = 3;
// the window release velocity is measured over, in ms
const VELOCITY_WINDOW = 100;
// the snap back: an overdamped spring that starts at rest, from wherever it was let go
const SPRING_STIFFNESS = 200;
const SPRING_DAMPING = 40;
const REST_DISTANCE = 1;
// a cancelled drag eases back in place
const CANCEL_DURATION = 300;
const CANCEL_EASE: CubicBezier = [0.25, 0.1, 0.35, 1];

/**
 * Drag down to dismiss, by touch: the content follows the finger down, resists being pulled up, springs
 * back when it's let go short of dismissing, and flies out when it's let go far enough down or fast enough.
 *
 * Call `start` with a pointer event to begin a drag, such as from `onPointerDown`, or later once a finger
 * is clearly heading down; the drag measures from that event, and other pointers are ignored. `cancel`
 * ends a drag and eases it back.
 */
export function useDragDismiss({
  ref,
  upElastic,
  dismissDistance,
  dismissVelocity = Infinity,
  flyOut,
  onDismiss,
}: UseDragDismissOptions) {
  const y = useRef(0);
  const exit = useRef(0);
  const session = useRef<DragSession | null>(null);
  const frame = useRef<number | null>(null);
  const removeListeners = useRef<(() => void) | null>(null);
  // the element last drawn on; a new one, such as a toast shown again after it was swiped away, starts at rest
  const drawnOn = useRef<HTMLElement | null>(null);
  // once a release dismisses, the element is on its way out: nothing restarts or cancels its fly-out
  const dismissed = useRef(false);
  // the latest options, so the window listeners don't go stale mid-drag
  const options = useRef({ upElastic, dismissDistance, dismissVelocity, flyOut, onDismiss });
  options.current = { upElastic, dismissDistance, dismissVelocity, flyOut, onDismiss };

  const render = useCallback(() => {
    const element = ref.current;
    if (!element) return;

    drawnOn.current = element;
    const active = y.current !== 0 || exit.current !== 0 || !!session.current?.started;
    element.style.setProperty('--drag-y', String(y.current));
    element.style.setProperty('--drag-exit', String(exit.current));
    element.toggleAttribute('data-dragging', active);
  }, [ref]);

  const stopAnimation = useCallback(() => {
    if (frame.current !== null) cancelAnimationFrame(frame.current);
    frame.current = null;
  }, []);

  // runs `step` once a frame with the ms since it started, until it returns true
  const animate = useCallback((step: (elapsed: number) => boolean) => {
    stopAnimation();
    let startTime: number | null = null;

    function tick(time: number) {
      if (startTime === null) startTime = time;
      const done = step(time - startTime);
      render();
      frame.current = done ? null : requestAnimationFrame(tick);
    }

    frame.current = requestAnimationFrame(tick);
  }, [render, stopAnimation]);

  const tweenTo = useCallback((to: number, duration: number, ease: CubicBezier, withExit = false) => {
    const from = y.current;
    const easing = cubicBezier(...ease);

    return new Promise<void>((resolve) => {
      animate((elapsed) => {
        const progress = easing(Math.min(elapsed / duration, 1));
        y.current = from + ((to - from) * progress);
        if (withExit) exit.current = progress;
        if (elapsed < duration) return false;
        resolve();
        return true;
      });
    });
  }, [animate]);

  const springBack = useCallback(() => {
    const from = y.current;
    const settle = overdampedSpring(from, SPRING_STIFFNESS, SPRING_DAMPING);

    animate((elapsed) => {
      y.current = settle(elapsed / 1000);
      if (Math.abs(y.current) > REST_DISTANCE) return false;
      y.current = 0;
      return true;
    });
  }, [animate]);

  const endSession = useCallback(() => {
    removeListeners.current?.();
    removeListeners.current = null;
    session.current = null;
  }, []);

  const handleMove = useCallback((e: PointerEvent) => {
    const current = session.current;
    if (!current || e.pointerId !== current.pointerId) return;

    const offsetY = e.clientY - current.startY;
    if (!current.started) {
      if (Math.hypot(e.clientX - current.startX, offsetY) < START_DISTANCE) return;
      current.started = true;
    }

    current.history.push({ y: e.clientY, time: e.timeStamp });
    const position = current.origin + offsetY;
    y.current = position < 0 ? position * options.current.upElastic : position;

    if (frame.current === null) {
      frame.current = requestAnimationFrame(() => {
        frame.current = null;
        render();
      });
    }
  }, [render]);

  const handleEnd = useCallback((e: PointerEvent) => {
    const current = session.current;
    if (!current || e.pointerId !== current.pointerId) return;
    endSession();
    if (!current.started) return;

    // a cancelled pointer's last position is its last move, not where it was cancelled
    const lastY = e.type === 'pointercancel' ? current.history[current.history.length - 1].y : e.clientY;
    const offsetY = lastY - current.startY;
    const velocityY = releaseVelocity(current.history);
    const { dismissDistance: distance, dismissVelocity: velocity, flyOut: out } = options.current;

    if (offsetY < distance && velocityY < velocity) {
      springBack();
      return;
    }

    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    dismissed.current = true;
    stopAnimation();
    options.current.onDismiss(
      reducedMotion ? Promise.resolve() : tweenTo(out.to(y.current), out.duration, out.ease, true),
    );
  }, [endSession, springBack, stopAnimation, tweenTo]);

  const start = useCallback((e: PointerEvent | React.PointerEvent) => {
    // touch only: with a mouse, a close button does the job, and dragging would keep text from being selected
    if (session.current || !e.isPrimary || e.pointerType !== 'touch') return;

    if (ref.current !== drawnOn.current) {
      y.current = 0;
      exit.current = 0;
      dismissed.current = false;
    }
    if (dismissed.current) return;

    stopAnimation();
    session.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      origin: y.current,
      started: false,
      history: [{ y: e.clientY, time: e.timeStamp }],
    };

    // heard on the window, so the drag carries on when the pointer leaves the element
    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleEnd);
    window.addEventListener('pointercancel', handleEnd);
    removeListeners.current = () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleEnd);
      window.removeEventListener('pointercancel', handleEnd);
    };
  }, [handleEnd, handleMove, ref, stopAnimation]);

  const cancel = useCallback(() => {
    if (dismissed.current) return;

    endSession();
    if (y.current !== 0) tweenTo(0, CANCEL_DURATION, CANCEL_EASE);
    // a drag that started without moving down is already in place, and only needs `data-dragging` cleared
    else render();
  }, [endSession, render, tweenTo]);

  useEffect(() => () => {
    endSession();
    stopAnimation();
  }, [endSession, stopAnimation]);

  return { start, cancel };
}

/**
 * The pointer's speed down, in px per second, over about the last `VELOCITY_WINDOW` ms: from the newest
 * point to the first one older than the window. A pointer held still before a flick would measure from
 * where it went down, so that point is skipped when it's well outside the window.
 */
export function releaseVelocity(history: { y: number; time: number }[]) {
  if (history.length < 2) return 0;

  const last = history[history.length - 1];
  let index = history.length - 1;
  while (index > 0 && last.time - history[index].time <= VELOCITY_WINDOW) index -= 1;

  let from = history[index];
  if (index === 0 && history.length > 2 && last.time - from.time > VELOCITY_WINDOW * 2) from = history[1];

  const seconds = (last.time - from.time) / 1000;
  return seconds > 0 ? (last.y - from.y) / seconds : 0;
}

/**
 * Where a spring that starts at rest at `from` is after `t` seconds, heading for 0. Overdamped, so it
 * eases in without passing 0.
 */
export function overdampedSpring(from: number, stiffness: number, damping: number) {
  const natural = Math.sqrt(stiffness);
  const ratio = damping / (2 * natural);
  const decay = natural * Math.sqrt((ratio * ratio) - 1);

  return (t: number) => {
    const envelope = Math.exp(-ratio * natural * t);
    return from * envelope * (Math.cosh(decay * t) + (((ratio * natural) / decay) * Math.sinh(decay * t)));
  };
}

// a CSS `cubic-bezier()` easing: progress over time, both from 0 to 1
export function cubicBezier(x1: number, y1: number, x2: number, y2: number) {
  // the curve's position at t, along one axis, with p1 and p2 its two control points on that axis
  const at = (t: number, p1: number, p2: number) => {
    const a = 1 - (3 * p2) + (3 * p1);
    const b = (3 * p2) - (6 * p1);
    const c = 3 * p1;
    return ((((a * t) + b) * t) + c) * t;
  };

  return (x: number) => {
    if (x <= 0 || x >= 1) return x;

    let low = 0;
    let high = 1;
    let t = x;
    for (let i = 0; i < 20; i += 1) {
      t = (low + high) / 2;
      if (at(t, x1, x2) < x) low = t;
      else high = t;
    }
    return at(t, y1, y2);
  };
}
